// API Security · Anomaly — finding detail side drawer (v3 shell).
// Mirrors PostureFindingDrawer: detail sections + Resolve/Suppress in footer.

import React from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, Button, Chip, Tooltip } from "@mui/material";
import { Activity } from "lucide-react";
import copy from "copy-to-clipboard";

import LayerdIcon from "../../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../../icons/detectionLucideIcons";
import { formatDate } from "../../../../../../utils/format";
import WafEventDrawerShell from "../../../detection/WafEventDrawerShell";
import FindingDrawerHeader from "../../../detection/v3/FindingDrawerHeader";
import SmartParseRawJsonViewer from "../../../detection/v3/SmartParseRawJsonViewer";
import {
  SmartParseSectionCard as SectionCard,
  DrawerFieldStack,
  spLh,
} from "../../../detection/v3/smartParseDrawerUi";
import {
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  secondaryButtonStyle,
  primaryButtonStyle,
  formatDrawerFooterLabel,
  tagChipSx,
} from "../../../detection/wafEventDrawerShared";
import { asecSeverityConfig, asecStatusDotColor, asecBtnOutlinedSm } from "../asecUi";

function FooterContext({ label }) {
  if (!label) return null;
  return (
    <Typography sx={{ fontSize: 13, lineHeight: spLh.px21, color: TEXT_SECONDARY, wordBreak: "break-all" }}>
      {label}
    </Typography>
  );
}

FooterContext.propTypes = { label: PropTypes.string };

function findingStatus(row) {
  if (row?.resolved_at) return "resolved";
  if (row?.suppressed_until) return "suppressed";
  return "open";
}

function prettifyKind(kind) {
  if (!kind) return "—";
  const parts = String(kind).split(".");
  if (parts.length < 2) return kind;
  const family = parts[0].toUpperCase();
  const tail = parts.slice(1).join(".").replace(/_/g, " ");
  return `${family} · ${tail[0].toUpperCase() + tail.slice(1)}`;
}

function SeverityChip({ severity }) {
  const c = asecSeverityConfig(severity);
  return (
    <Chip
      size="small"
      label={c.label}
      sx={{
        ...tagChipSx,
        height: 22,
        backgroundColor: c.bgColor,
        color: c.color,
        borderRadius: "30px",
        fontSize: 12,
        fontWeight: 500,
      }}
    />
  );
}

SeverityChip.propTypes = { severity: PropTypes.string };

function StatusChip({ status }) {
  const label = status[0].toUpperCase() + status.slice(1);
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: asecStatusDotColor(status) }} />
      <Typography sx={{ fontSize: 13, color: TEXT_PRIMARY }}>{label}</Typography>
    </Stack>
  );
}

StatusChip.propTypes = { status: PropTypes.string.isRequired };

export default function AnomalyFindingDrawer({
  open,
  onClose,
  row,
  onResolve,
  onSuppressRequest,
  actionSaving = false,
}) {
  const [snackOpen, setSnackOpen] = React.useState(false);

  const handleCopy = (value) => {
    if (!value || value === "—" || value === "-") return;
    copy(String(value));
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  if (!row) {
    return (
      <WafEventDrawerShell open={false} onClose={onClose} title="Anomaly Finding">
        {null}
      </WafEventDrawerShell>
    );
  }

  const status = findingStatus(row);
  const canResolve = !row.resolved_at;
  const canSuppress = !row.resolved_at && !row.suppressed_until;
  const detail = row.detail && typeof row.detail === "object" ? row.detail : {};
  const detailEntries = Object.entries(detail).filter(([, v]) => v != null && v !== "");
  const identityLabel = row.identity_kind
    ? `${row.identity_kind}:${row.identity_id || ""}`
    : null;

  const handleResolve = async () => {
    if (!onResolve) return;
    const ok = await onResolve(row.id);
    if (ok) onClose();
  };

  const footerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
      <Button
        onClick={onClose}
        disabled={actionSaving}
        startIcon={<LayerdIcon icon={DETECTION_ICONS.close} size={16} color="neutral" />}
        sx={secondaryButtonStyle}
      >
        Close
      </Button>
      {canSuppress && (
        <Tooltip title="Requires a reason · suppressed for 7 days">
          <span>
            <Button
              onClick={() => onSuppressRequest?.(row.id)}
              disabled={actionSaving}
              sx={asecBtnOutlinedSm}
            >
              Suppress
            </Button>
          </span>
        </Tooltip>
      )}
      {canResolve && (
        <Button
          onClick={handleResolve}
          disabled={actionSaving}
          sx={primaryButtonStyle}
        >
          {actionSaving ? "Saving…" : "Resolve"}
        </Button>
      )}
    </Stack>
  );

  return (
    <>
      <WafEventDrawerShell
        open={open}
        onClose={onClose}
        title="Anomaly Finding"
        headerVariant="hero"
        headerContent={(
          <FindingDrawerHeader
            embedded
            title={prettifyKind(row.finding_kind)}
            method={row.method}
            path={row.path_template}
            detailLine={row.host || row.site_id || undefined}
            icon={Activity}
            idLabel="ID"
            idValue={row.id}
            onCopyId={handleCopy}
          />
        )}
        footerLeft={(
          <FooterContext label={formatDrawerFooterLabel(null, row.host || row.site_id)} />
        )}
        footerActions={footerActions}
      >
        <SectionCard icon={DETECTION_ICONS.detection} title="Finding">
          <DrawerFieldStack
            columns={2}
            onCopy={handleCopy}
            fields={[
              {
                key: "kindLabel",
                label: "Anomaly",
                value: prettifyKind(row.finding_kind),
              },
              {
                key: "kind",
                label: "Kind",
                value: row.finding_kind,
                copyable: true,
                monospace: true,
              },
              {
                key: "severity",
                label: "Severity",
                content: <SeverityChip severity={row.severity} />,
              },
              {
                key: "status",
                label: "Status",
                content: <StatusChip status={status} />,
              },
              {
                key: "detected",
                label: "Detected",
                value: row.detected_at ? formatDate(row.detected_at) : "—",
              },
              row.resolved_at
                ? {
                  key: "resolved",
                  label: "Resolved",
                  value: formatDate(row.resolved_at),
                }
                : null,
              row.suppressed_until
                ? {
                  key: "suppressedUntil",
                  label: "Suppressed until",
                  value: formatDate(row.suppressed_until),
                }
                : null,
              row.suppression_reason
                ? {
                  key: "suppressReason",
                  label: "Suppression reason",
                  value: row.suppression_reason,
                  fullRow: true,
                }
                : null,
              row.suppression_actor
                ? {
                  key: "suppressActor",
                  label: "Suppressed by",
                  value: row.suppression_actor,
                }
                : null,
            ]}
          />
        </SectionCard>

        <SectionCard icon={DETECTION_ICONS.request} title="Endpoint">
          <DrawerFieldStack
            columns={2}
            onCopy={handleCopy}
            fields={[
              { key: "host", label: "Host", value: row.host, copyable: true },
              { key: "method", label: "Method", value: row.method },
              {
                key: "path",
                label: "Path template",
                value: row.path_template,
                copyable: true,
                monospace: true,
                fullRow: true,
              },
              row.endpoint_id
                ? {
                  key: "endpointId",
                  label: "Endpoint ID",
                  value: row.endpoint_id,
                  copyable: true,
                  monospace: true,
                  fullRow: true,
                }
                : null,
              row.site_id
                ? {
                  key: "siteId",
                  label: "Site ID",
                  value: row.site_id,
                  copyable: true,
                }
                : null,
            ]}
          />
        </SectionCard>

        {identityLabel && (
          <SectionCard icon={DETECTION_ICONS.ja3} title="Identity">
            <DrawerFieldStack
              columns={2}
              onCopy={handleCopy}
              fields={[
                {
                  key: "identityKind",
                  label: "Identity kind",
                  value: row.identity_kind,
                },
                {
                  key: "identityId",
                  label: "Identity ID",
                  value: row.identity_id,
                  copyable: true,
                  monospace: true,
                  fullRow: true,
                },
              ]}
            />
          </SectionCard>
        )}

        {detailEntries.length > 0 && (
          <SectionCard icon={DETECTION_ICONS.clipboard} title="Detail">
            <DrawerFieldStack
              columns={2}
              onCopy={handleCopy}
              fields={detailEntries.map(([key, value]) => ({
                key,
                label: key.replace(/_/g, " "),
                value: typeof value === "object" ? JSON.stringify(value) : String(value),
                copyable: true,
                fullRow: typeof value === "object" || String(value).length > 48,
                monospace: typeof value === "object",
              }))}
            />
          </SectionCard>
        )}

        <SectionCard icon={DETECTION_ICONS.info} title="Raw data" defaultExpanded={false}>
          <SmartParseRawJsonViewer
            data={row}
            onCopy={handleCopy}
            filename="anomaly_finding.json"
          />
        </SectionCard>
      </WafEventDrawerShell>

      {snackOpen && (
        <Box
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            backgroundColor: TEXT_PRIMARY,
            color: "#fff",
            px: 2,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1400,
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#fff" }}>
            Copied to clipboard!
          </Typography>
        </Box>
      )}
    </>
  );
}

AnomalyFindingDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  row: PropTypes.object,
  onResolve: PropTypes.func,
  onSuppressRequest: PropTypes.func,
  actionSaving: PropTypes.bool,
};
